import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { dedupeBlogEntriesById, sortBlogEntriesByPublishedDate } from '../utils/blog-published-date';

/**
 * Listing modes for different taxonomy types
 */
export type ListingMode = 'all' | 'topic' | 'content-series' | 'subject' | 'discipline';

/**
 * Sort options for blog queries
 */
export type SortOrder = 'newest' | 'oldest' | 'title-asc' | 'title-desc';

/**
 * Options for querying blogs
 */
export interface BlogQueryOptions {
  mode: ListingMode;
  filterId?: string;
  filterValue?: string;
  limit?: number;
  offset?: number;
  sortOrder?: SortOrder;
  /** Optional AbortSignal to cancel the underlying fetch request. */
  signal?: AbortSignal;
}

/**
 * Result from blog query
 */
export interface BlogQueryResult {
  results: any[];
  /** Total matches reported by Builder (when present). */
  count: number;
  /**
   * True when the API returned at least `limit` raw rows for this request.
   * Used for pagination: after sort/dedupe, `results.length` can be &lt; limit even when more pages exist.
   */
  apiReturnedFullPage: boolean;
}

/**
 * BlogListingService - Handles fetching and filtering blog content
 * 
 * Centralizes all blog listing logic with:
 * - Multiple filter modes (topic, series, subject, discipline)
 * - Server-side pagination
 * - Count estimation with caching
 * - Flexible query building
 */
@Injectable({
  providedIn: 'root'
})
export class BlogListingService {
  private readonly apiKey = environment.builderApiKey;
  private readonly baseUrl = 'https://cdn.builder.io/api/v3/content/blog';
  
  // Cache for total counts (reduces API calls)
  private countCache = new Map<string, { count: number; timestamp: number }>();
  private readonly CACHE_TTL = 5 * 60 * 1000; // 5 minutes

  /**
   * Fetch blogs with pagination and filtering
   * OPTIMIZATION: Now returns the total count from API response
   */
  async fetchBlogs(options: BlogQueryOptions): Promise<BlogQueryResult> {
    const { 
      mode, 
      filterId, 
      filterValue, 
      limit = 21, 
      offset = 0,
      sortOrder = 'newest',
      signal
    } = options;
    
    // Build query URL
    let url = `${this.baseUrl}?apiKey=${this.apiKey}` +
              `&limit=${limit}` +
              `&offset=${offset}` +
              this.getSortParam(sortOrder) +
              `&includeRefs=true` +
              `&noTargeting=true`;
              // REMOVED: &cachebust=true - let browser/CDN cache work
    
    // Add filter based on mode
    const queryParam = this.buildQueryParam(mode, filterId, filterValue);
    if (queryParam) {
      url += queryParam;
    }
    
    console.log('📡 Blog Query URL:', url);
    
    try {
      const response = await fetch(url, { signal });
      const data = await response.json();
      
      // OPTIMIZATION: Use actual count if available, fallback to length
      const count = typeof data.count === 'number' ? data.count : (data.results?.length || 0);
      const rawResults = data.results || [];
      const apiReturnedFullPage = rawResults.length >= limit;

      // Re-sort by real calendar time. Builder/CDN can return rows ordered as strings
      // when publishedDate formats differ (e.g. month-name strings sort alphabetically).
      let results = rawResults;
      if (sortOrder === 'newest') {
        results = sortBlogEntriesByPublishedDate(rawResults, 'desc') as any[];
      } else if (sortOrder === 'oldest') {
        results = sortBlogEntriesByPublishedDate(rawResults, 'asc') as any[];
      }

      results = dedupeBlogEntriesById(results) as any[];

      return {
        results,
        count,
        apiReturnedFullPage
      };
    } catch (error: any) {
      // Propagate AbortError so callers can detect intentional cancellation
      if (error?.name === 'AbortError') {
        throw error;
      }
      console.error('❌ Error fetching blogs:', error);
      return {
        results: [],
        count: 0,
        apiReturnedFullPage: false
      };
    }
  }
  
  /**
   * Build query parameter based on mode and filter
   */
  private buildQueryParam(
    mode: ListingMode, 
    filterId?: string, 
    filterValue?: string
  ): string {
    // If "All" selected or no filter, check for existence only
    if (!filterId || filterId === 'ALL' || filterValue === 'All') {
      // Return blogs that have this field populated
      const existsMap: Record<ListingMode, string> = {
        'subject': '&query.data.subject.id.$exists=true',
        'topic': '&query.data.topicthemepairs.topic.id.$exists=true',
        'content-series': '&query.data.contentSeries.id.$exists=true',
        'discipline': '&query.data.discipline.id.$exists=true',
        'all': '' // No filter
      };
      return existsMap[mode];
    }
    
    // Filter by specific ID
    const fieldMap: Record<ListingMode, string> = {
      'subject': 'query.data.subject.id',
      'topic': 'query.data.topicthemepairs.topic.id',
      'content-series': 'query.data.contentSeries.id',
      'discipline': 'query.data.discipline.id',
      'all': ''
    };
    
    const field = fieldMap[mode];
    return field ? `&${field}=${encodeURIComponent(filterId)}` : '';
  }
  
  /**
   * Get sort parameter for API
   */
  private getSortParam(sortOrder: SortOrder): string {
    const sortMap: Record<SortOrder, string> = {
      'newest': '&sort.data.publishedDate=-1',
      'oldest': '&sort.data.publishedDate=1',
      'title-asc': '&sort.data.title=1',
      'title-desc': '&sort.data.title=-1'
    };
    return sortMap[sortOrder];
  }
  
  /**
   * Get estimated total count (with caching)
   * 
   * Strategy: Instead of fetching ALL records, we:
   * 1. Check cache first
   * 2. Make a single query with large limit (100)
   * 3. If we get < 100, that's the count
   * 4. If we get 100, estimate based on pattern
   */
  async getEstimatedCount(options: BlogQueryOptions): Promise<number> {
    const cacheKey = `${options.mode}-${options.filterId || 'all'}`;
    
    // Check cache
    const cached = this.countCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < this.CACHE_TTL) {
      console.log('📦 Using cached count:', cached.count);
      return cached.count;
    }
    
    console.log('🔍 Fetching new count estimate...');
    
    // Fetch with large limit to estimate
    const firstBatch = await this.fetchBlogs({
      ...options,
      limit: 100,
      offset: 0
    });
    
    let estimatedCount = firstBatch.count;
    
    // If we got 100, there are likely more - fetch one more batch
    if (firstBatch.count === 100) {
      const secondBatch = await this.fetchBlogs({
        ...options,
        limit: 100,
        offset: 100
      });
      
      estimatedCount = 100 + secondBatch.count;
      
      // If second batch is also 100, there are definitely more
      if (secondBatch.count === 100) {
        // Make one more attempt to get closer estimate
        const thirdBatch = await this.fetchBlogs({
          ...options,
          limit: 100,
          offset: 200
        });
        estimatedCount = 200 + thirdBatch.count;
      }
    }
    
    // Cache result
    this.countCache.set(cacheKey, {
      count: estimatedCount,
      timestamp: Date.now()
    });
    
    console.log('💾 Cached new count:', estimatedCount);
    return estimatedCount;
  }
  
  /**
   * Clear cache (call when new blog posts are published)
   */
  clearCache(): void {
    this.countCache.clear();
    console.log('🗑️ Count cache cleared');
  }
  
  /**
   * Clear cache for specific filter
   */
  clearCacheForFilter(mode: ListingMode, filterId?: string): void {
    const cacheKey = `${mode}-${filterId || 'all'}`;
    this.countCache.delete(cacheKey);
    console.log('🗑️ Cache cleared for:', cacheKey);
  }
}

